import type { CallbackEvent } from "@shopify/polaris-types";
import { stripShopifyGid } from "@codway-dev/shared";
import type {
  DiscountType,
  Placement,
  TriggerType,
} from "../../../store/one-click-editor.mapping";
import type { TriggerProduct } from "../../../store/triggers.mapping";
import {
  CollapsibleSectionCard,
  SectionHeader,
  Divider,
  NumericField,
  ProductPickerButton,
} from "../../../components/shared/EditorShared";
import { openTriggerProductsPicker } from "../../../utils/resource-picker.utils";

export interface ProductVariant {
  id: string;
  title: string;
  price: string;
}

export interface SelectedProduct {
  id: string;
  title: string;
  price: string;
  imageUrl?: string;
  description?: string;
  variants: ProductVariant[];
}

// ─── Section 1: Name ─────────────────────────────────────────────────────────

export function NameSection({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={1} title="Nombre del upsell" />}
    >
      <s-stack direction="block" gap="base">
        <s-text-field
          label="Nombre interno"
          value={value}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            onChange(e.currentTarget.value)
          }
        />
        <s-text color="subdued">
          Este nombre es solo para ti, tus clientes no lo verán.
        </s-text>
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Section 2: Trigger ───────────────────────────────────────────────────────

const TRIGGER_OPTIONS: { value: TriggerType; label: string }[] = [
  { value: "all", label: "Todos los productos" },
  { value: "specific_products", label: "Productos específicos" },
];

const PLACEMENT_OPTIONS: { value: Placement; label: string }[] = [
  { value: "before_complete", label: "Pre-compra" },
  { value: "after_complete", label: "Post-compra" },
];

export function TriggerSection({
  value,
  onChange,
  products,
  onProductsChange,
  placement,
  onPlacementChange,
}: {
  value: TriggerType;
  onChange: (t: TriggerType) => void;
  products: TriggerProduct[];
  onProductsChange: (products: TriggerProduct[]) => void;
  placement: Placement;
  onPlacementChange: (p: Placement) => void;
}) {
  const onPickerError = () =>
    window.shopify?.toast?.show("No se pudo abrir el selector de productos.", {
      isError: true,
    });

  const removeProduct = (id: string) =>
    onProductsChange(products.filter((p) => p.id !== id));

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={2} title="¿Cuándo mostrar este upsell?" />}
    >
      <s-stack direction="block" gap="base">
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">¿Dónde aparece este upsell?</s-text>
          <s-text color="subdued">
            Elige si el upsell se muestra antes o después de que el cliente
            complete el pedido.
          </s-text>

          <div className="flex bg-gray-100 p-1 rounded-lg">
            {PLACEMENT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onPlacementChange(opt.value)}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                  placement === opt.value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </s-stack>

        <Divider />

        <s-text color="subdued">
          Elige si este upsell aparece para cualquier compra o solo cuando el
          cliente tiene productos específicos en su pedido.
        </s-text>

        <div className="flex bg-gray-100 p-1 rounded-lg">
          {TRIGGER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                value === opt.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {value === "specific_products" && (
          <s-stack direction="block" gap="small-200">
            <ProductPickerButton
              label={
                products.length > 0
                  ? `${products.length} producto(s) seleccionado(s)`
                  : "Seleccionar productos disparadores"
              }
              onClick={() =>
                openTriggerProductsPicker(
                  products,
                  onProductsChange,
                  onPickerError
                )
              }
            />

            {products.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50"
              >
                {p.imageUrl ? (
                  <img
                    src={p.imageUrl}
                    alt={p.title}
                    className="w-10 h-10 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <s-icon type="image" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {p.title}
                  </p>
                </div>
                <button
                  onClick={() => removeProduct(p.id)}
                  className="text-xs text-red-600 hover:text-red-800 font-medium flex-shrink-0"
                >
                  Quitar
                </button>
              </div>
            ))}

            {products.length === 0 && (
              <s-text color="subdued">
                Selecciona al menos un producto para activar este disparador.
              </s-text>
            )}
          </s-stack>
        )}
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Section 3: Product & Discount ───────────────────────────────────────────

async function openProductPicker(
  onSelect: (product: SelectedProduct) => void,
  onError?: (err: unknown) => void
) {
  try {
    const selected = await window.shopify?.resourcePicker({
      type: "product",
      multiple: false,
    });
    if (!selected || selected.length === 0) return;
    const p = selected[0];
    onSelect({
      id: stripShopifyGid(p.id),
      title: p.title,
      price: p.variants[0]?.price ?? "0.00",
      imageUrl: p.images[0]?.originalSrc,
      description: p.description,
      variants: p.variants.map((v) => ({
        id: stripShopifyGid(v.id),
        title: v.title,
        price: v.price,
      })),
    });
  } catch (err) {
    onError?.(err);
  }
}

export function ProductDiscountSection({
  selectedProduct,
  onProductSelect,
  discountType,
  onDiscountTypeChange,
  discountValue,
  onDiscountValueChange,
}: {
  selectedProduct: SelectedProduct | null;
  onProductSelect: (product: SelectedProduct) => void;
  discountType: DiscountType;
  onDiscountTypeChange: (d: DiscountType) => void;
  discountValue: number;
  onDiscountValueChange: (v: number) => void;
}) {
  const onPickerError = () =>
    window.shopify?.toast?.show("No se pudo abrir el selector de productos.", {
      isError: true,
    });

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={3} title="Producto & Descuento" />}
    >
      <s-stack direction="block" gap="base">
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Producto a ofrecer</s-text>

          {selectedProduct ? (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
              {selectedProduct.imageUrl ? (
                <img
                  src={selectedProduct.imageUrl}
                  alt={selectedProduct.title}
                  className="w-10 h-10 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <s-icon type="image" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {selectedProduct.title}
                </p>
                <p className="text-xs text-gray-500">
                  ${selectedProduct.price}
                </p>
              </div>
              <button
                onClick={() =>
                  openProductPicker(onProductSelect, onPickerError)
                }
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex-shrink-0"
              >
                Cambiar
              </button>
            </div>
          ) : (
            <ProductPickerButton
              label="Seleccionar producto"
              onClick={() => openProductPicker(onProductSelect, onPickerError)}
            />
          )}
        </s-stack>

        <Divider />

        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Descuento</s-text>
          <s-select
            label="Tipo de descuento"
            value={discountType}
            onChange={(e: CallbackEvent<"s-select">) => {
              onDiscountTypeChange(e.currentTarget.value as DiscountType);
              onDiscountValueChange(0);
            }}
          >
            <s-option value="none">Sin descuento</s-option>
            <s-option value="percentage_off">Porcentaje de descuento</s-option>
            <s-option value="fixed_amount_off">
              Monto fijo de descuento
            </s-option>
            <s-option value="fixed_price_per_item">
              Precio fijo por unidad
            </s-option>
          </s-select>

          {discountType === "percentage_off" && (
            <NumericField
              label="Porcentaje (%)"
              min={0}
              max={100}
              value={discountValue}
              onChange={onDiscountValueChange}
            />
          )}
          {(discountType === "fixed_amount_off" ||
            discountType === "fixed_price_per_item") && (
            <NumericField
              label="Monto"
              min={0}
              value={discountValue}
              onChange={onDiscountValueChange}
            />
          )}
        </s-stack>
      </s-stack>
    </CollapsibleSectionCard>
  );
}
